import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/intake.css";

export default function PaymentPolicyForm({ clinicId, formData, setFormData }) {
  const [isEditing, setIsEditing] = useState(true);
  const [openPayments, setOpenPayments] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [depositRequired, setDepositRequired] = useState(null);
  const [insuranceAccepted, setInsuranceAccepted] = useState(null);
  const [financingAvailable, setFinancingAvailable] = useState(null);
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [refundPolicy, setRefundPolicy] = useState("");
  const [packagePolicy, setPackagePolicy] = useState("");
  const [membershipPolicy, setMembershipPolicy] = useState("");

  const paymentOptions = [
    "Cash",
    "Credit Card",
    "Debit Card",
    "HSA/FSA",
    "Apple Pay",
    "CareCredit",
    "Zelle",
    "Venmo",
    "Other",
  ];

  const togglePayment = (method) => {
    if (selectedPayments.includes(method)) {
      setSelectedPayments(selectedPayments.filter((m) => m !== method));
    } else {
      setSelectedPayments([...selectedPayments, method]);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        clinic_id: clinicId,
        accepted_payment_methods: selectedPayments,
        deposit_required: depositRequired,
        insurance_accepted: insuranceAccepted,
        financing_available: financingAvailable,
        cancellation_policy: cancellationPolicy,
        refund_policy: refundPolicy,
        package_pricing_policy: packagePolicy,
        membership_policy: membershipPolicy,
      };

      console.log("Payload being sent:", payload);
      console.log("typeof clinicId:", typeof clinicId, "value:", clinicId); 


      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/payment-policy/save-payment-policy`,
        payload
      );

      setFormData((prev) => ({
        ...prev,
        payment_policy: {
          accepted_payment_methods: selectedPayments,
          deposit_required: depositRequired,
          insurance_accepted: insuranceAccepted,
          financing_available: financingAvailable,
          cancellation_policy: cancellationPolicy,
          refund_policy: refundPolicy,
          package_pricing_policy: packagePolicy,
          membership_policy: membershipPolicy,
        },
      }));
      

      alert("Payment & policy information saved!");
      setIsEditing(false);
    } catch (err) {
      console.error("Error saving payment policy", err);
      alert("Failed to save payment policy.");
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    const fetchPaymentPolicy = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/payment-policy/get-payment-policy`,
          { params: { clinic_id: clinicId } }
        );
  
        if (res.data.status === "success" && res.data.data) {
          const policy = res.data.data;
          setSelectedPayments(policy.accepted_payment_methods || []);
          setDepositRequired(policy.deposit_required);
          setInsuranceAccepted(policy.insurance_accepted);
          setFinancingAvailable(policy.financing_available);
          setCancellationPolicy(policy.cancellation_policy || "");
          setRefundPolicy(policy.refund_policy || "");
          setPackagePolicy(policy.package_pricing_policy || "");
          setMembershipPolicy(policy.membership_policy || "");
          setIsEditing(false);
        }
      } catch (err) {
        console.error("Error loading payment policy", err);
      }
    };
  
    if (clinicId) fetchPaymentPolicy();
  }, [clinicId]);
  

  return (
    <div className="form-wrapper">
      <div className="form-section-header">
        <h2>
          5. <span className="section-title">Payment & Policy Information</span>
        </h2>
        <div className="form-buttons">
          {isEditing ? (
            <button className="blue-button" onClick={handleSave}>Save</button>
          ) : (
            <button className="blue-button" onClick={handleEdit}>Edit</button>
          )}
        </div>
      </div>

      <div className="separator" />

      <div className="form-grid">
        <div className="form-field full-width">
          <label>Accepted Payment Methods<span className="required">*</span></label>
          <div className="dropdown-wrapper">
            <div
              className="dropdown-header"
              onClick={() => isEditing && setOpenPayments(!openPayments)}
            >
              {selectedPayments.length > 0
                ? selectedPayments.join(", ")
                : "Select payment methods..."}
              <span className="arrow">{openPayments ? "▲" : "▼"}</span>
            </div>
            {openPayments && isEditing && (
              <div className="dropdown-list">
                {paymentOptions.map((method) => (
                  <div className="dropdown-item2" key={method}>
                    <input
                      type="checkbox"
                      checked={selectedPayments.includes(method)}
                      onChange={() => togglePayment(method)}
                    />
                    {method}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="policy-options-container">
          <div className="form-field radio-group-inline">
            <label>Deposit required?</label>
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="deposit"
                  disabled={!isEditing}
                  checked={depositRequired === true}
                  onChange={() => setDepositRequired(true)}
                /> Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="deposit"
                  disabled={!isEditing}
                  checked={depositRequired === false}
                  onChange={() => setDepositRequired(false)}
                /> No
              </label>
            </div>
          </div>

          <div className="form-field radio-group-inline">
            <label>Insurance Accepted?</label>
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="insurance"
                  disabled={!isEditing}
                  checked={insuranceAccepted === true}
                  onChange={() => setInsuranceAccepted(true)}
                /> Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="insurance"
                  disabled={!isEditing}
                  checked={insuranceAccepted === false}
                  onChange={() => setInsuranceAccepted(false)}
                /> No
              </label>
            </div>
          </div>

          <div className="form-field radio-group-inline">
            <label>Financing Available?</label>
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="financing"
                  disabled={!isEditing}
                  checked={financingAvailable === true}
                  onChange={() => setFinancingAvailable(true)}
                /> Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="financing"
                  disabled={!isEditing}
                  checked={financingAvailable === false}
                  onChange={() => setFinancingAvailable(false)}
                /> No
              </label>
            </div>
          </div>

          <div className="form-field full-width">
            <label>Cancellation Policy</label>
            <textarea
              rows="15"
              disabled={!isEditing}
              value={cancellationPolicy}
              onChange={(e) => setCancellationPolicy(e.target.value)}
            />
          </div>

          <div className="form-field full-width">
            <label>Refund Policy</label>
            <textarea
              rows="15"
              disabled={!isEditing}
              value={refundPolicy}
              onChange={(e) => setRefundPolicy(e.target.value)}
            />
          </div>

          <div className="form-field full-width">
            <label>Package Pricing Policy</label>
            <textarea
              rows="15"
              disabled={!isEditing}
              value={packagePolicy}
              onChange={(e) => setPackagePolicy(e.target.value)}
            />
          </div>

          <div className="form-field full-width">
            <label>Membership Policy</label>
            <textarea
              rows="15"
              disabled={!isEditing}
              value={membershipPolicy}
              onChange={(e) => setMembershipPolicy(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

